import { ITelemetryContext } from './sinks/types/ITelemetryContext';
import { ITelemetrySink } from './sinks/types/ITelemetrySink';
import { stringifyHelper } from './sinks/utils/stringifyHelper';

// tslint:disable-next-line:no-any
type FunctionType = (...args: any[]) => any;

export type FunctionPropKeys<T> = {
  [K in keyof Required<T>]: Required<T>[K] extends FunctionType ? K : never;
}[keyof T];

type FunctionPropValues<T> = {
  [K in FunctionPropKeys<T>]: Required<T>[K] extends FunctionType ? Required<T>[K] : never;
};

/**
 * Calls a telemetry sink function (e.g. 'log') on the supplied array of sinks, with
 * error handling logic if one of the telemetry sinks errors.
 *
 * @param functionName The name of the ITelemetrySink function to call on each sink.
 * @param functionParams The params to pass to the ITelemetrySink function for each sink.
 * @param telemetrySinksToCall The telemetry sinks.
 */
export function callFunctionOnTelemetrySinks<
  TContext extends ITelemetryContext,
  TFunctionName extends FunctionPropKeys<ITelemetrySink>,
  TFunction extends FunctionType = FunctionPropValues<ITelemetrySink>[TFunctionName]
>(
  functionName: TFunctionName,
  functionParams: Parameters<TFunction>,
  telemetrySinksToCall: ITelemetrySink[],
  context?: TContext,
  depth = 0
): void {
  const logErrorWithRemainingTelemetrySinks = (error: unknown, erroredSinkIndex: number): void => {
    const erroredSink = telemetrySinksToCall[erroredSinkIndex];

    const errorName = 'TelemetrySinkFailure';
    const errorMessage = `Failed to execute function '${functionName}' on TelemetrySink '${
      erroredSink && erroredSink.id
    }'`;

    // Limit the recursion depth to 1
    // If it exceeds, directly log to the console
    if (depth > 1) {
      console.log(errorName, errorMessage);
      return;
    }
    // Failure while trying to log an event with a telemetry sink - try to log the failure with other telemetry Sinks
    const remainingTelemetrySinks = [
      ...telemetrySinksToCall.slice(0, erroredSinkIndex),
      ...telemetrySinksToCall.slice(erroredSinkIndex + 1, telemetrySinksToCall.length)
    ];

    if (remainingTelemetrySinks.length) {
      callFunctionOnTelemetrySinks(
        'error',
        [
          {
            name: errorName,
            data: {
              name: errorName,
              message: errorMessage
            },
            context
          }
        ],
        remainingTelemetrySinks,
        context,
        depth + 1
      );
    } else {
      throw new Error(`[${errorName}] ${errorMessage} \n${stringifyHelper(error)}`);
    }
  };

  telemetrySinksToCall.map((sink, index) => {
    try {
      const fn = sink[functionName] as TFunction | undefined;
      if (fn) {
        fn(...functionParams);
      }
    } catch (e) {
      return logErrorWithRemainingTelemetrySinks(e, index);
    }
  });
}
